import { useEffect } from "preact/hooks";
import { GameMode, HEROES, Role, Team, otherTeam, type MatchSummary, type PlayerStats } from "@rivalwatch/shared";
import { heroTheme } from "../audio/ui.ts";
import { RoleIcon } from "./icons.tsx";
import { HeroPortrait } from "./Portrait.tsx";
import { eliminations, FINAL_HITS, TeamBoard } from "./Scoreboard.tsx";
import { ui } from "./store.ts";

type SummaryPlayer = MatchSummary["players"][number];

/** A single impact score for picking MVP/SVP: output and eliminations, less deaths. */
const impact = (s: PlayerStats) =>
  s.kills * 250 + s.finalHits * 100 + s.assists * 150 + s.damage + s.healing + s.mitigated * 0.5 + (s.objectiveTicks / 60) * 25 - s.deaths * 200;

const best = (ps: SummaryPlayer[]) => ps.reduce<SummaryPlayer | undefined>((b, p) => (!b || impact(p.stats) > impact(b.stats) ? p : b), undefined);

/** Post-match: verdict banner, MVP (winning team) and SVP (losing team), then both team boards. */
export function MatchEnd() {
  const s = ui.matchEnd.value;
  const me = ui.lobby.value?.players.find((p) => p.id === ui.you.value);
  const myTeam = me?.team ?? Team.A;
  const draw = !s || s.winner === Team.None;
  const won = !!s && s.winner === myTeam;
  // The summary needs a cursor: release the mouse as soon as it appears. As the fanfare settles,
  // your side's standout gets their theme — the MVP if you won, your SVP if you didn't.
  useEffect(() => {
    if (!s) return;
    if (document.pointerLockElement) document.exitPointerLock();
    const star = best(s.players.filter((p) => (draw ? true : p.team === myTeam)));
    if (star) heroTheme(star.hero, "heroic", won || draw ? 2.8 : 3.4, "music");
  }, [s]);
  if (!s) return null;
  const fi = myTeam === Team.B ? 1 : 0;
  const close = () => (ui.matchEnd.value = undefined);
  const mvp = best(draw ? s.players : s.players.filter((p) => p.team === s.winner));
  const svp = draw ? undefined : best(s.players.filter((p) => p.team === otherTeam(s.winner)));
  const tone = draw ? "draw" : won ? "win" : "loss";
  const convoy = s.mode === GameMode.Convoy;
  // Control's score is rounds won. Convoy's headline is how far each team pushed (a tenth of a
  // percent can decide it); its notes count checkpoints, or the delivery.
  const pushed = (i: number) => {
    const v = s.progress[i] ?? 0;
    return v >= 100 ? "100%" : `${(Math.floor(v * 10) / 10).toFixed(1)}%`;
  };
  const headline = (i: number) => (convoy ? pushed(i) : String(s.score[i]));
  const scoreNote = (n: number, team: Team) => {
    if (!convoy) return `${n} ${n === 1 ? "round" : "rounds"}`;
    if ((s.progress[team === Team.A ? 0 : 1] ?? 0) >= 100) return "Delivered";
    return `${n} ${n === 1 ? "checkpoint" : "checkpoints"}`;
  };
  return (
    <div class={`overlay rv-end-wrap ${tone}`} onClick={(e) => e.target === e.currentTarget && close()}>
      <section class="rv-end" aria-label="Match summary">
        <header class="rv-end-head">
          <div class="rv-end-verdict">
            <span>Match over</span>
            <h2>{draw ? "Draw" : won ? "Victory" : "Defeat"}</h2>
          </div>
          <div class={`rv-end-score${convoy ? " pct" : ""}`} role="img" aria-label={`${convoy ? "Pushed" : "Rounds won"}: you ${headline(fi)}, enemy ${headline(1 - fi)}`}>
            <b class="ally">{headline(fi)}</b>
            <i />
            <b class="foe">{headline(1 - fi)}</b>
          </div>
          <button class="rv-btn" data-sound="back" onClick={close}>
            Back to warmup
          </button>
        </header>
        <div class="rv-end-body">
          <div class="rv-end-stars">
            {mvp && <StarCard label="MVP" p={mvp} friend={mvp.team === myTeam} />}
            {svp && <StarCard label="SVP" p={svp} friend={svp.team === myTeam} />}
          </div>
          <div class="rv-board">
            {[myTeam, otherTeam(myTeam)].map((team) => {
              const players = s.players.filter((p) => p.team === team);
              const rounds = s.score[team === myTeam ? fi : 1 - fi];
              return (
                <TeamBoard
                  key={team}
                  friend={team === myTeam}
                  note={`${scoreNote(rounds, team)} · ${eliminations(players)} eliminations`}
                  tailLabel={convoy ? "Payload" : "On point"}
                  extra={[FINAL_HITS]}
                  players={players.map((p, i) => ({
                    key: `${p.name}-${i}`,
                    name: p.name,
                    hero: p.hero,
                    stats: p.stats,
                    me: p.name === me?.name,
                    tail: `${Math.round(p.stats.objectiveTicks / 60)}s`,
                  }))}
                />
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}

function StarCard({ label, p, friend }: { label: "MVP" | "SVP"; p: SummaryPlayer; friend: boolean }) {
  const def = HEROES[p.hero];
  const s = p.stats;
  const [statName, statValue] = def.role === Role.Support ? ["Healing", s.healing] : def.role === Role.Tank ? ["Blocked", s.mitigated] : ["Damage", s.damage];
  return (
    <article class={`rv-star ${label.toLowerCase()} ${friend ? "ally" : "foe"}`} style={{ "--hc": def.color }} aria-label={`${label}: ${p.name}, ${def.name}`}>
      <em aria-hidden="true">{label}</em>
      <HeroPortrait hero={p.hero} />
      <div class="rv-star-body">
        <b>{p.name}</b>
        <small>
          <RoleIcon role={def.role} size={14} />
          {def.name}
        </small>
        <dl>
          <div>
            <dt>KO / D / A</dt>
            <dd>
              {s.kills}/{s.deaths}/{s.assists}
            </dd>
          </div>
          <div>
            <dt>{statName}</dt>
            <dd>{Math.round(statValue).toLocaleString()}</dd>
          </div>
        </dl>
      </div>
    </article>
  );
}
